import { AccountContextState } from '../components/apps/account/context/AccountProvider';
import SubmitResult from '../enums/SubmitResults';
import IFormSubmitResult from '../interfaces/IFormSubmitResult';
import { GlobalContextData } from './../contexts/GlobalContext';
import { ILocalizationContext } from './../contexts/LocalizationContext';
import { IErrorContext, SendErrorToBackend } from './errorTracker';

export type SuccessResponse = (result: IFormSubmitResult) => void;
export type FailedResponse = (result: IFormSubmitResult) => void;

/** Délai au bout duquel une requête est abandonnée (4 minutes). */
const requestTimeoutMs = 60000 * 4;

/**
 * Sentinelle levée lorsqu'un échec a déjà été signalé à l'appelant via `failed`.
 * Le `.catch` terminal la reconnaît et n'appelle pas `failed` une seconde fois.
 */
const alreadyReported = { alreadyReported: true };

/** Vérifie sans risque si la réponse désérialisée est un échec métier (`result` peut être `null`). */
function isSubmissionFailed(result: unknown): boolean {
    return typeof result === 'object' && result !== null && 'submissionFailed' in result && (result as IFormSubmitResult).submissionFailed;
}

/**
 * Appelle le callback de succès en isolant ses exceptions : une erreur levée par le consommateur
 * n'est pas un échec de transport et ne doit donc pas déclencher `failed` après coup.
 */
function invokeSuccess<T>(success: (result: T) => void, result: T): void {
    try {
        success(result);
    } catch (e) {
        console.error('The success callback threw an exception.', e);
    }
}

/** 
 * Handles the exception by trying to send to the server the failed response to consolidate error tracking
 * @param result Result of the form submission
 * @param url URL where the form was submitted
 * @param failed Callback to call after handling the error
 * @param account Account context to provide user information
 * @param error Optional error object that triggered the failure if available
 * **/
const handleFailedResponse = (
    result: IFormSubmitResult,
    url: string,
    failed: FailedResponse,
    account: AccountContextState | undefined,
    error?: Error | undefined) : void => {

    try {

        let errorMessage = `Form submission failed with these errors : ${result.errors?.map(err => [err.summary, err.detailed].join(' --- ')).join('\r\n') || 'no error detected '} `;

        if (account?.account) {
            errorMessage += `for account this : ${account.account.username}`;
        }
        
        const currentException: IErrorContext = {
            message: errorMessage,
            stack: error?.stack,
            url: url,
            userAgent: navigator.userAgent
        }

        const context = [];
        
        if (error) {
            context.push({ error });
        }

        if (account?.account) {
            // Copy account without the property profilePicture
            let { profilePicture, ...accountProfile } = account.account;
            context.push({ account: accountProfile });
        }

        SendErrorToBackend(currentException, context);

    } catch (e) {
        console.error('Failed to send error to backend', e);
    }

    failed(result);
};

/**
 * Sends a POST request containing the form content and attachments to the specified URL (default timeout 60 seconds)
 * @param url URL to submit the form to
 * @param form Form data object that will be serialized into JSON
 * @param files Array of files or [] if attachments aren't supported by the form
 * @param global Global context to display the loading state
 * @param account Account context to send if an error happen
 * @param success Called when the submission is successful
 * @param failed Called when the submission fails
 * @param localization Localization context to provide the language used (optional)
 */
function submitForm<T>(
    url: string,
    form: any,
    files: File[],
    global: GlobalContextData | undefined,
    account: AccountContextState | undefined,
    success: (result: T) => void,
    failed: FailedResponse,
    localization?: ILocalizationContext,
    method?: 'POST' | 'PUT'
): void {

    global?.setLoading(true);

    const formData = new FormData();
    if (form) formData.append('data', JSON.stringify(form));
    for (let i = 0; i < files.length; i++) {
        formData.append('attachments', files[i]);
    }

    const langQueryParam = localization ? (url.includes('?') ? `&lang=${localization.lang}` : `?lang=${localization.lang}`) : '';

    const usedMethod: string = method ?? 'POST';

    fetchWithTimeout(
        `${url}${langQueryParam}`,
        {
            timeout: requestTimeoutMs,
            method: usedMethod,
            // add generic header used by most AJAX library to detect when call is ajax
            headers: { 'x-requested-with': 'XMLHttpRequest', 'client-time-zone': Intl.DateTimeFormat().resolvedOptions().timeZone },
            body: formData,
        },
        () =>
            handleFailedResponse(
                getGenericException(`The ${usedMethod} request to '${url}' has timed out after ${requestTimeoutMs / 1000} seconds.`),
                url,
                failed,
                account
            )
    )
        .then((r: Response) => {
            if (r.redirected && r.url?.includes('Account/AccessDenied?')) {
                const accessDeniedResponse: string =
                    localization?.strings.accessDenied ||
                    `Vous n'avez pas accès à ces informations. | You don't have access to see these information.`;
                failed(getGenericException(accessDeniedResponse));
                throw alreadyReported;
            }
            if (r.status === 401 && r.headers.get('CustomReason') === 'redirected-identity-from-ajax') {
                const sessionExpiredResponse: string =
                    localization?.strings.sessionExpired ||
                    `Votre session a expiré. Veuillez actualiser la page avant de continuer.. | Your session has expired. Please refresh the page before continuing.`;
                failed(getGenericException(sessionExpiredResponse));
                throw alreadyReported;
            }
            if (!r.ok) {
                handleFailedResponse(getGenericException(r.toString(), r.toString()), url, failed, account);
                throw alreadyReported;
            }

            return r.json();
        })
        .then((result: T) => {
            if (isSubmissionFailed(result)) {
                handleFailedResponse(result as unknown as IFormSubmitResult, url, failed, account);
                console.error(result);
                return;
            }

            invokeSuccess(success, result);
        })
        .catch((ex: unknown) => {
            // L'échec a déjà été signalé (accès refusé, session expirée, HTTP non-OK, expiration du délai).
            if (ex === alreadyReported) return;

            const summary: string = ex instanceof Error ? ex.message : String(ex);
            handleFailedResponse(
                getGenericException(`An exception was thrown when sending ${usedMethod} request to ${url}. ${summary}`, summary),
                url,
                failed,
                account,
                ex instanceof Error ? ex : undefined
            );
        })
        .then(() => {
            // Exécuté quelle que soit l'issue : le voile de chargement ne doit jamais rester bloqué.
            global?.setLoading(false);
        });
}

/**
 * Submits data to the specified URL (default timeout 15 seconds)
 * @param url URL to submit the data to
 * @param data Data object that will be serialized into JSON, method will be GET if no data is provided
 * @param global Global context to display the loading state
 * @param account Account context for additionnal data
 * @param success Called when the submission is successful
 * @param failed Called when the submission fails
 */
function submitRequest<T>(
    url: string,
    data: any,
    global: GlobalContextData | undefined,
    account: AccountContextState | undefined,
    success: (result: T) => void,
    failed: (result: IFormSubmitResult) => void,
    httpMethod?: 'POST' | 'GET' | 'DELETE' | 'PUT'
): void {

    global?.setLoading(true);

    const method: string = httpMethod ? httpMethod : data === undefined ? 'GET' : 'POST';

    const fetchOption: RequestInit & { timeout: number } = {
        timeout: requestTimeoutMs,
        method,
        // add generic header used by most AJAX library to detect when call is ajax
        headers: {
            'Content-Type': 'application/json',
            'x-requested-with': 'XMLHttpRequest',
            'client-time-zone': Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        body: JSON.stringify(data),
    };

    if (data?.attachments?.length > 0 || data?.forceFormData) {
        const formData = new FormData();
        if (data) formData.append('data', JSON.stringify(data));

        for (let i = 0; i < data?.attachments?.length; i++) {
            formData.append('attachments', data.attachments[i]);
        }

        fetchOption.headers = undefined;
        fetchOption.body = formData;
    }

    fetchWithTimeout(url, fetchOption, () =>
        handleFailedResponse(
            getGenericException(`The ${method} request to '${url}' has timed out after ${requestTimeoutMs / 1000} seconds.`),
            url,
            failed,
            account
        )
    )
        .then((res: Response) => {
            if (res.redirected && res.url?.includes('Account/AccessDenied?')) {
                const accessDeniedResponse: string = `Vous n'avez pas accès à ces informations. | You don't have access to see these information.`;
                handleFailedResponse(getGenericException(accessDeniedResponse), url, failed, account);
                throw alreadyReported;
            }
            if (res.status === 401 && res.headers.get('CustomReason') === 'redirected-identity-from-ajax') {
                const sessionExpiredResponse: string = `Votre session a expiré. Veuillez actualiser la page avant de continuer.. | Your session has expired. Please refresh the page before continuing.`;
                handleFailedResponse(getGenericException(sessionExpiredResponse), url, failed, account);
                throw alreadyReported;
            }
            if (!res.ok) {
                const badResponse: string = `Expected HTTP response 200 OK but received HTTP ${res.status}${
                    res.statusText ? ` (${res.statusText})` : ''
                }.`;
                handleFailedResponse(getGenericException(badResponse), url, failed, account);
                throw alreadyReported;
            }

            return res.json();
        })
        .then((result: T) => {
            if (isSubmissionFailed(result)) {
                handleFailedResponse(result as unknown as IFormSubmitResult, url, failed, account);
                console.error(result);
                return;
            }

            invokeSuccess(success, result);
        })
        .catch((ex: unknown) => {
            // L'échec a déjà été signalé (accès refusé, session expirée, HTTP non-OK, expiration du délai).
            if (ex === alreadyReported) return;

            const summary: string = ex instanceof Error ? ex.message : String(ex);
            handleFailedResponse(
                getGenericException(`An exception was thrown when sending ${method} request to ${url}. ${summary}`, summary),
                url,
                failed,
                account,
                ex instanceof Error ? ex : undefined
            );
        })
        .then(() => {
            global?.setLoading(false);
        });
}

/**
 * Generates an IFormSubmitResult that contains a single error
 * @param summary Summary of the error
 * @param detailed Detailed version of the error, if any
 * @returns IFormSubmitResult with a single error and where all systems are N/A
 */
function getGenericException(summary: string, detailed?: string): IFormSubmitResult {
    console.error(summary);

    return {
        database: SubmitResult.NotApplicable,
        email: SubmitResult.NotApplicable,
        mFiles: SubmitResult.NotApplicable,
        pdf: SubmitResult.NotApplicable,
        storage: SubmitResult.NotApplicable,
        submissionFailed: true,
        genericException: true,
        errors: [
            {
                summary,
                detailed: detailed ?? summary,
            },
        ],
    };
}

/**
 * Improves the standard fetch API to allow for setting up a timeout after which is request is killed and onTimeout callback is called
 * @param url URL to fetch
 * @param options Options to pass to the fetch API & a timeout value in ms (defaults to 8000)
 * @param onTimeout Callback that gets called if the timeout is reached
 * @returns Response of the fetch request
 */
async function fetchWithTimeout(url: string, options: RequestInit & { timeout: number }, onTimeout: () => void): Promise<Response> {
    const { timeout = 8000 } = options;

    const controller = new AbortController();
    let timedOut: boolean = false;

    const id = setTimeout(() => {
        timedOut = true;
        controller.abort();
    }, timeout);

    try {
        return await fetch(url, {
            ...options,
            signal: controller.signal,
        });
    } catch (e) {
        // L'abandon provoqué par le délai remonte ici sous forme d'AbortError : on ne le signale
        // qu'une seule fois, via onTimeout, puis on lève la sentinelle pour éviter un second appel.
        if (timedOut) {
            onTimeout();
            throw alreadyReported;
        }

        throw e;
    } finally {
        // Sans ce nettoyage, un échec réseau rapide laisse le minuteur armé et onTimeout
        // se déclenche plusieurs minutes plus tard, hors contexte.
        clearTimeout(id);
    }
}

export default submitForm;
export { submitRequest };
